import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useActionsInstruction, useDestinataires, useInstructions, useTableauDeBord, type InstructionSaisie } from '@/api/hooks';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { InstructionsTable } from '@/components/courrier';
import { InstructionChamps, instructionVide } from '@/components/instructions-builder';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SelectNatif } from '@/components/ui/select-natif';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

type Vue = 'toutes' | 'nouvelles' | 'en-cours' | 'echeance' | 'retard' | 'reponse' | 'cloturees';
const VUES: Array<[Vue, string]> = [
  ['toutes', 'Toutes'],
  ['nouvelles', 'Nouvelles'],
  ['en-cours', 'En cours'],
  ['echeance', 'À échéance'],
  ['retard', 'En retard'],
  ['reponse', 'Réponses à valider'],
  ['cloturees', 'Clôturées'],
];

/** Registre des instructions : les miennes (responsable), celles de ma direction (directeur), toutes (DG, assistante, admin). */
export function InstructionsRegistre() {
  const session = useSession();
  const u = session!.utilisateur;
  const navigate = useNavigate();
  const tdb = useTableauDeBord();
  const destinataires = useDestinataires();
  const actions = useActionsInstruction();
  const toutes = tdb.data?.courrier.instructions.perimetre === 'TOUTES';
  const peutEmettre = Boolean(tdb.data?.courrier.parapheur) || u.profils.includes('ASSISTANT') || u.profils.includes('ADMINISTRATEUR');
  const [vue, setVue] = useState<Vue>('toutes');
  const [responsableId, setResponsableId] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [nouvelle, setNouvelle] = useState<InstructionSaisie>(instructionVide());
  const { data, isLoading, error } = useInstructions({ vue, responsableId: responsableId || undefined });

  const emettre = async () => {
    try {
      const r = await actions.creerLibre.mutateAsync({ responsableId: nouvelle.responsableId, type: nouvelle.type, texte: nouvelle.texte.trim(), echeance: nouvelle.echeance, priorite: nouvelle.priorite });
      toast.success(`Instruction ${r.instruction.reference} émise.`);
      setOuvert(false);
      setNouvelle(instructionVide());
      navigate(`/instructions/${r.instruction.id}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Émission impossible.');
    }
  };

  return (
    <div>
      <PageHeader
        titre="Registre des instructions"
        sousTitre={
          toutes
            ? "Toutes les instructions du Directeur Général : état d'exécution, échéances, rappels et escalades."
            : tdb.data?.courrier.instructions.perimetre === 'MIENNES'
              ? 'Les instructions qui vous sont adressées, ou adressées à votre direction.'
              : undefined
        }
        actions={
          peutEmettre && (
            <Button onClick={() => setOuvert(true)}>
              <Plus className="size-4" /> Nouvelle instruction
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={vue} onValueChange={(v) => setVue(v as Vue)}>
          <TabsList className="flex-wrap">
            {VUES.map(([v, l]) => (
              <TabsTrigger key={v} value={v}>
                {l}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {toutes && (
          <SelectNatif className="w-72" value={responsableId} onChange={(e) => setResponsableId(e.target.value)} aria-label="Responsable">
            <option value="">Tous les responsables</option>
            {(destinataires.data ?? []).map((d) => (
              <option key={d.agentId} value={d.agentId}>
                {d.nom} · {d.fonction}
              </option>
            ))}
          </SelectNatif>
        )}
      </div>
      {isLoading ? <Chargement /> : error ? <ErreurBloc erreur={error} /> : <div className="rounded-lg border bg-card">{<InstructionsTable instructions={data ?? []} vide="Aucune instruction dans cette vue." />}</div>}

      <Dialog open={ouvert} onOpenChange={setOuvert}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Instruction hors courrier</DialogTitle>
            <DialogDescription>Instruction orale ou issue d'une réunion, inscrite au registre avec un responsable et une échéance.</DialogDescription>
          </DialogHeader>
          <InstructionChamps valeur={nouvelle} onChange={setNouvelle} priorite />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOuvert(false)}>Fermer</Button>
            <Button onClick={emettre} disabled={!nouvelle.responsableId || nouvelle.texte.trim().length < 5 || actions.creerLibre.isPending}>
              Émettre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
